import { ChangeEvent, CSSProperties, useEffect, useState } from 'react';
import {
  backButton,
  hapticFeedbackImpactOccurred,
  miniApp
} from '@telegram-apps/sdk-react';
import { Trans, useTranslation } from 'react-i18next';
import TextareaAutosize from 'react-textarea-autosize';

import UIConfig from '@/UIConfig';
import { CandidateProfilePhotosDto } from '@/api/matchmaker/types.ts';
import { catchErrors } from '@/utils/sentry-monitor.tsx';
import MessagingController from '@/api/messaging/messaging.ts';
import { useLikesSentListStore } from '@/store/useLikesSentListStore.ts';
import Modal from '@/animations/Modal.tsx';
import PaywallPopup from '@/components/Popup/PaywallPopup/PaywallPopup.tsx';
import { PaywallTypes, ProductInvoicePlacements } from '@/enums/apiEnums.ts';
import { helpers } from '@/helpers.ts';
import { MessageTypesUI } from '@/enums/viewEnums.ts';
import { SentGiftSuccessToast } from '@/components/Toast/SendGiftSuccessToast.tsx';
import { ImageWithLoader } from '@/components/common/Image/ImageWithLoader.tsx';
import FallingStarShape from '@/components/common/svg/shapes/FallingStarShape.tsx';
import SmallStarsShape from '@/components/common/svg/shapes/SmallStarsShape.tsx';
import FireReactionShape from '@/components/common/svg/shapes/FireReactionShape.tsx';
import { useHeaderStore } from '@/store/useHeadersStylesStore.ts';
import { MainButton } from '@/components/common/MainButton/MainButton.tsx';
import { useKeyboardLift } from '@/hooks/useKeyboardLift.ts';
import { useBackButtonToCloseModal } from '@/hooks/useBackButtonToCloseModal.tsx';
import { useSummariesStore } from '@/store/useSummariesStore.ts';

const SendGiftPopup = ({
  giftedContactId,
  giftedContactName,
  giftedContactPhoto,
  likesMe,
  handleClose,
  haptic = true,
  remainingAmount,
  placement,
  hideBackButton,
  prevHeaderTemplate = 'dark'
}: {
  giftedContactId: string;
  giftedContactName: string;
  giftedContactPhoto?: CandidateProfilePhotosDto;
  likesMe?: boolean;
  handleClose: (swipeRight?: boolean) => void;
  haptic?: boolean;
  remainingAmount: number;
  placement: ProductInvoicePlacements;
  hideBackButton: boolean;
  prevHeaderTemplate?: 'dark' | 'light';
}) => {
  useBackButtonToCloseModal(handleClose, hideBackButton);

  const { t } = useTranslation();
  const [isSending, setIsSending] = useState(false);
  const [text, setText] = useState('');
  const [payWallOpen, setPayWallOpen] = useState<boolean>(false);

  const { getLikesCount } = useSummariesStore();
  const { removeSentLike } = useLikesSentListStore();
  const { setHeaderColors, switchHeaderColors } = useHeaderStore();

  const {
    offset,
    textAreaRef,
    inputHandlers,
    stableHeight,
    contentPaddingBottom
  } = useKeyboardLift({
    barHeight: 70
  });

  useEffect(() => {
    const previousBgColor = miniApp.backgroundColor();
    const previousHeaderColor = miniApp.headerColor();

    miniApp.setBackgroundColor('#1D1D1D');
    miniApp.setHeaderColor('#1D1D1D');

    return () => {
      miniApp.setBackgroundColor(previousBgColor);
      miniApp.setHeaderColor(previousHeaderColor);
    };
  }, []);

  const sendGift = async () => {
    try {
      await MessagingController.sendGift(giftedContactId, {
        text
      });

      if (likesMe) {
        void getLikesCount(); // if send like to person who likes me - update likes count
      }

      helpers.toast({
        id: MessageTypesUI.TYPE_GIFT,
        content: (
          <SentGiftSuccessToast
            name={giftedContactName}
            photoUrl={helpers.getPrimaryPhoto(
              giftedContactPhoto?.variants ?? []
            )}
          />
        )
      });

      handleClose(true);

      if (hideBackButton && backButton.isVisible()) {
        backButton.hide();
      }
    } catch (e) {
      catchErrors(e, true);
    }
  };

  /**
   * @param swipeRight - needs to control cases when the popups must be closed without swiping
   */
  const closeAllPopups = (swipeRight?: boolean) => {
    if (hideBackButton && backButton.isVisible()) {
      backButton.hide();
    }

    setPayWallOpen(false);
    handleClose(swipeRight);
  };

  const onSend = async () => {
    if (remainingAmount > 0) {
      setIsSending(true);

      if (haptic) {
        hapticFeedbackImpactOccurred('rigid');
      }

      try {
        await sendGift();
      } catch (e) {
        catchErrors(e, true);
      } finally {
        setIsSending(false);
      }

      // remove from sent likes list if we send it from there
      removeSentLike(giftedContactId);
    } else {
      // Remove focus from textarea to close keyboard before opening paywall
      if (textAreaRef.current) {
        textAreaRef.current.blur();
      }
      setPayWallOpen(true);
    }
  };

  const afterPaywallPaidAction = async () => {
    await sendGift();
    closeAllPopups(true);
  };

  useEffect(() => {
    setHeaderColors({
      backgroundColor: UIConfig.Colors.BackgroundDark,
      logoColor: UIConfig.Colors.BackgroundWhite
    });
    return () => {
      if (prevHeaderTemplate === 'light') {
        switchHeaderColors('light');
      }

      if (prevHeaderTemplate === 'dark') {
        switchHeaderColors('dark');
      }
    };
  }, []);

  const handleTextChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
  };

  const handleFocus = () => {
    if (inputHandlers.onFocus) {
      inputHandlers.onFocus();
    }
  };

  const handleBlur = () => {
    if (inputHandlers.onBlur) {
      inputHandlers.onBlur();
    }
  };

  return (
    <>
      <div
        style={{
          ...containerStyle,
          height: stableHeight
        }}
      >
        <div style={contentStyle}>
          <div style={imageContainerStyle}>
            <ImageWithLoader
              fullWidth={false}
              style={imageStyle}
              src={helpers.getPrimaryPhoto(giftedContactPhoto?.variants ?? [])}
              thumbhash={helpers.getThumbHash(
                giftedContactPhoto?.variants ?? []
              )}
              alt="Contact"
            />
            <div style={diamondContainerStyle}>
              <FireReactionShape width={20} height={20} fillColor="#ffffff" />
            </div>

            <div style={{ position: 'absolute', top: -25, left: -30 }}>
              <FallingStarShape height={40} width={40} />
            </div>
            <div style={{ position: 'absolute', bottom: -5, right: -10 }}>
              <SmallStarsShape height={28} width={17} />
            </div>
          </div>
          <div style={textContainerStyle}>
            <p style={titleStyle}>{t('banner.send_gift')}</p>
            <p style={{ margin: '15px auto', maxWidth: '80%' }}>
              <Trans
                i18nKey="banner.attract_user"
                components={{
                  bold: <b />,
                  br: <br />
                }}
                values={{ name: giftedContactName }}
              />
            </p>
            <div style={textareaContainerStyle}>
              <TextareaAutosize
                ref={textAreaRef}
                minRows={2}
                maxRows={2}
                disabled={isSending}
                style={textareaStyle}
                maxLength={140}
                placeholder={t('banner.gift_text_placeholder')}
                onChange={handleTextChange}
                {...inputHandlers}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
              <div style={charCounterStyle}>{text.length || 0}/140</div>
            </div>
          </div>
        </div>
        {/*<div style={closeButtonStyle}>*/}
        {/*  <CloseModalButton handleClose={handleCloseButtonClick} />*/}
        {/*</div>*/}

        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            padding: '5px 18px 15px 18px',
            transform: `translateY(-${offset - contentPaddingBottom + 40}px)`,
            minHeight: '7%',
            paddingBottom: contentPaddingBottom + 40
          }}
          onMouseDown={(e) => {
            e.preventDefault();
          }}
        >
          <MainButton
            text={t('profile.send_gift_banner.button')}
            onClick={onSend}
            buttonStyle={{ backgroundColor: '#FEBF39', color: '#000000' }}
            loading={isSending}
          />
        </div>
      </div>

      <Modal
        fullScreen
        open={payWallOpen}
        handleClose={() => closeAllPopups(false)}
      >
        <PaywallPopup
          handleClose={() => closeAllPopups(false)}
          type={PaywallTypes.TYPE_ONE_TIME}
          callAfterPaywallAction={afterPaywallPaidAction}
          placement={placement}
          hideBackButton={false}
          prevHeaderTemplate={prevHeaderTemplate}
        />
      </Modal>
    </>
  );
};

const containerStyle: CSSProperties = {
  width: '100%',
  position: 'relative',
  background: UIConfig.Colors.BackgroundDark
};

const contentStyle: CSSProperties = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center'
};

const imageContainerStyle: CSSProperties = {
  position: 'relative',
  marginTop: '25px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
};

const imageStyle: CSSProperties = {
  width: '100px',
  height: '100px',
  borderRadius: '50%',
  objectFit: 'cover',
  zIndex: 2
};

const diamondContainerStyle: CSSProperties = {
  position: 'absolute',
  width: 30,
  height: 30,
  background: '#FFB634',
  bottom: -15,
  right: '50%',
  transform: 'translateX(50%)',
  borderRadius: '50%',
  border: '3px solid #1d1d1d',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 3
};

const textContainerStyle: CSSProperties = {
  color: '#fff',
  textAlign: 'center',
  margin: '15px 0',
  width: '100%'
};

const titleStyle: CSSProperties = {
  fontSize: '24px',
  fontWeight: 600,
  margin: 0
};

const textareaContainerStyle: CSSProperties = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: '15px'
};

const textareaStyle = {
  width: '100%',
  maxWidth: '80%',
  padding: '14px',
  fontSize: '16px',
  resize: 'none' as const,
  outline: 'none',
  fontFamily: UIConfig.Fonts.Primary,
  background: 'rgba(255, 255, 255, 0.1)',
  border: 'none',
  color: '#FFF',
  borderRadius: '10px',
  lineHeight: '20px'
};

const charCounterStyle: CSSProperties = {
  color: '#fff',
  fontSize: '12px',
  marginTop: '5px',
  width: '80%',
  textAlign: 'right'
};

export default SendGiftPopup;